import React, { useRef } from "react";
import { X } from "lucide-react";

import ModalDialog from "../ModalDialog";

interface DeleteFileDialogProps {
	fileName: string;
	dealName: string;
	onCancel: () => void;
	onConfirm: () => void;
}

const TITLE_ID = "delete-file-title";

/**
 * Confirms deleting one file from a deal (DOP-608). A delete cannot be undone,
 * so it is always one deliberate click away — and the default focus is Cancel.
 */
const DeleteFileDialog: React.FC<DeleteFileDialogProps> = ({
	fileName,
	dealName,
	onCancel,
	onConfirm,
}) => {
	const cancelRef = useRef<HTMLButtonElement>(null);

	return (
		<ModalDialog titleId={TITLE_ID} onClose={onCancel} initialFocusRef={cancelRef}>
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h2 id={TITLE_ID} className="text-xl font-semibold text-text-base">
						Delete this file?
					</h2>
					<p className="mt-1 break-words text-sm text-text-muted">
						<span className="font-medium text-text-strong">{fileName}</span> will
						be removed from {dealName}. New answers won't use it. Answers you
						already have keep their sources.
					</p>
				</div>
				<button
					type="button"
					onClick={onCancel}
					aria-label="Close"
					className="rounded-md p-1.5 text-text-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					<X className="h-[18px] w-[18px]" aria-hidden="true" />
				</button>
			</div>
			<div className="mt-6 flex justify-end gap-2.5">
				<button
					ref={cancelRef}
					type="button"
					onClick={onCancel}
					className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={onConfirm}
					className="rounded-md bg-negative-fg px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-negative-fg focus-visible:ring-offset-2"
				>
					Delete file
				</button>
			</div>
		</ModalDialog>
	);
};

export default DeleteFileDialog;
